/**
 * Pushback Node Configuration - creates a draft, approval-tracked request to push a
 * value back into a Codebeamer field.
 */

import React, { useEffect, useState } from 'react';
import { pipelineApi } from '../api/client';
import type { PipelineNode, PipelineDefinition } from '../types/pipeline';
import { AlmTargetPicker } from './AlmTargetPicker';
import type { AlmTarget } from './AlmTargetPicker';
import { ValueBindingEditor, isBindingComplete, normalizeBinding } from './ValueBindingEditor';
import type { OutputValueBindingConfig } from './ValueBindingEditor';
import {
  PUSHBACK_OPERATIONS,
  PushbackLoopEditor,
  emptyPushbackLoop,
  isPushbackLoopComplete,
  normalizeElementBinding,
} from './PushbackLoopEditor';
import type { PushbackLoopConfig } from './PushbackLoopEditor';
import './PushbackConfig.css';
import './CreateItemConfig.css';

const OPERATION_OPTIONS = PUSHBACK_OPERATIONS;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type PushbackValueBindingConfig = OutputValueBindingConfig;

export interface PushbackNodeConfig {
  // Single value mode: exactly one of target_field (a fixed field name) or target_field_binding
  // (field name read from e.g. an upstream node's output) is set, plus value_binding.
  // Loop mode: only `loop` is set - mirrors PushbackStep.
  target_field?: string;
  target_field_binding?: PushbackValueBindingConfig;
  operation: (typeof OPERATION_OPTIONS)[number];
  value_binding?: PushbackValueBindingConfig;
  loop?: PushbackLoopConfig;
  // Optional target tracker - when set, the item's source_item is looked up in this tracker
  // instead of the item's own resolved scope.
  target_scope_id?: string;
  target_project_id?: string;
  target_type_id?: string;
}

interface PushbackConfigProps {
  node: PipelineNode;
  pipeline: PipelineDefinition | null;
  availableWorkflowInputs: string[];
  onSave: (config: PushbackNodeConfig) => void;
  onClose: () => void;
}

export const PushbackConfig: React.FC<PushbackConfigProps> = ({
  node,
  pipeline,
  availableWorkflowInputs,
  onSave,
  onClose,
}) => {
  const existing = node.config as Partial<PushbackNodeConfig> | undefined;
  const existingBinding = existing?.value_binding;

  const [targetFieldBinding, setTargetFieldBinding] = useState<OutputValueBindingConfig>(
    existing?.target_field_binding ?? { source: 'literal', literal_value: existing?.target_field ?? '' }
  );
  const [operation, setOperation] = useState<PushbackNodeConfig['operation']>(existing?.operation ?? 'OVERRIDE');
  const [binding, setBinding] = useState<OutputValueBindingConfig>(
    existingBinding ?? { source: 'literal', literal_value: '' }
  );
  const [mode, setMode] = useState<'single' | 'loop'>(existing?.loop ? 'loop' : 'single');
  const [loop, setLoop] = useState<PushbackLoopConfig>(existing?.loop ?? emptyPushbackLoop());
  const [itemFields, setItemFields] = useState<string[]>([]);
  const [target, setTarget] = useState<AlmTarget>({
    scopeId: existing?.target_scope_id ?? '',
    projectId: existing?.target_project_id ?? '',
    typeId: existing?.target_type_id ?? '',
  });

  useEffect(() => {
    pipelineApi
      .getOutputItemFieldNames()
      .then(setItemFields)
      .catch(() => setItemFields([]));
  }, []);

  const targetSet = target.scopeId.trim() !== '' || target.typeId.trim() !== '' || target.projectId.trim() !== '';
  const targetScopeValid = UUID_PATTERN.test(target.scopeId.trim());
  const targetValid = !targetSet || (targetScopeValid && target.typeId.trim() !== '');
  const canSave =
    targetValid &&
    (mode === 'loop' ? isPushbackLoopComplete(loop) : isBindingComplete(targetFieldBinding) && isBindingComplete(binding));

  const handleSave = () => {
    if (!canSave) return;

    const valueConfig =
      mode === 'loop'
        ? {
            loop: {
              items_binding: loop.items_binding,
              target_field: normalizeElementBinding(loop.target_field),
              value: normalizeElementBinding(loop.value),
              ...(loop.operation && { operation: normalizeElementBinding(loop.operation) }),
            },
          }
        : {
            // A literal target field is saved as the plain field name, as before bindings existed.
            ...(targetFieldBinding.source === 'literal'
              ? { target_field: (targetFieldBinding.literal_value ?? '').trim() }
              : { target_field_binding: normalizeBinding(targetFieldBinding) }),
            value_binding: normalizeBinding(binding),
          };

    onSave({
      ...valueConfig,
      operation,
      ...(targetSet && {
        target_scope_id: target.scopeId.trim(),
        target_project_id: target.projectId.trim(),
        target_type_id: target.typeId.trim(),
      }),
    });
    onClose();
  };

  return (
    <div className="pushback-config">
      <div className="config-header">
        <div>
          <h3>{node.label || 'Pushback'}</h3>
          <p className="section-description">
            Creates a DRAFT pushback request to a Codebeamer field - approval happens outside raven
          </p>
        </div>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="config-body">
        <div className="config-section">
          <h4>Target Tracker (optional)</h4>
          <p className="field-hint">
            Tracker the source items live in. Leave empty to use each item's own synced scope.
          </p>
          <AlmTargetPicker value={target} onChange={setTarget} />
          {targetSet && !targetValid && (
            <p className="field-error">A target tracker needs a valid scope UUID and a tracker/type id.</p>
          )}
        </div>

        <div className="config-section">
          <div className="condition-row">
            <label>Mode</label>
            <select
              className="condition-input"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'single' | 'loop')}
            >
              <option value="single">Single value</option>
              <option value="loop">For each item in an upstream list</option>
            </select>
          </div>
        </div>

        {mode === 'loop' ? (
          <PushbackLoopEditor
            loop={loop}
            onChange={setLoop}
            operation={operation}
            onOperationChange={setOperation}
            pipeline={pipeline}
            nodeId={node.id}
          />
        ) : (
        <div className="config-section">
          <div className="condition-row">
            <label>Target Field</label>
            <ValueBindingEditor
              idPrefix={`${node.id}-pushback-target`}
              binding={targetFieldBinding}
              onChange={setTargetFieldBinding}
              pipeline={pipeline}
              nodeId={node.id}
              availableWorkflowInputs={availableWorkflowInputs}
              itemFields={itemFields}
            />
          </div>

          <div className="condition-row">
            <label>Operation</label>
            <select
              className="condition-input"
              value={operation}
              onChange={(e) => setOperation(e.target.value as PushbackNodeConfig['operation'])}
            >
              {OPERATION_OPTIONS.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          </div>

          <div className="condition-row">
            <label>Value Source</label>
            <ValueBindingEditor
              idPrefix={`${node.id}-pushback`}
              binding={binding}
              onChange={setBinding}
              pipeline={pipeline}
              nodeId={node.id}
              availableWorkflowInputs={availableWorkflowInputs}
              itemFields={itemFields}
            />
          </div>
        </div>
        )}

        <div className="config-help">
          <h4>How this works</h4>
          <ul>
            <li>
              Creates a <strong>draft</strong> <code>DataPushbackRequest</code> - the field write only happens once
              something outside raven approves it.
            </li>
            <li>
              <strong>Upstream node output</strong> reads a field of a connected node's result and adds a required
              dependency on it. For agents with a JSON output schema, pick one of its keys.
            </li>
            <li>
              The <strong>target field</strong> can come from an upstream node too - its output must be the name of
              the Codebeamer field to update.
            </li>
            <li>
              <strong>For each item in an upstream list</strong> creates one draft per element of a list in an
              upstream node's output, e.g. an agent returning{' '}
              <code>{'{"pushbacks": [{"field": ..., "value": ...}]}'}</code>. An empty list creates nothing; failed
              items are reported together and fail the node.
            </li>
          </ul>
        </div>
      </div>

      <div className="config-footer">
        <button className="button-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="button-primary" onClick={handleSave} disabled={!canSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
};
